import {
    getFacadeContentQuery,
    GetFacadeContentQuery,
    getPageAndGlobalProps,
    RedirectPageParams,
    NotFoundParams,
    isPageNotFound,
    isRedirect,
    getPageFromIdString,
    PageType,
    hotelPageQuery,
    HotelPageQuery,
    isRedirectHotel,
    GlobalContent,
    PageRedirects,
    isRerouteGlobal,
    parseBoolean,
    useGlobalContext,
    hasFeatureFlag,
    FeatureFlagEnum,
} from '@honeycomb/data';
import { BetaHotelPage, CmsContentPage, HotelPage } from '@honeycomb/ui';
import { GetServerSidePropsContext } from 'next';

export default function Landing(data: GetFacadeContentQuery | HotelPageQuery) {
    const {
        configSettings: { EnableBetaEstabPage },
    } = useGlobalContext();

    if ('getEstabById' in data) {
        const { getEstabById, metaData } = data;
        const betaPageEnabled =
            (parseBoolean(EnableBetaEstabPage) || hasFeatureFlag(FeatureFlagEnum.BETA_ESTAB_PAGE)) &&
            !hasFeatureFlag(FeatureFlagEnum.OLD_ESTAB_PAGE);
        return betaPageEnabled ? (
            <BetaHotelPage getEstabById={getEstabById} metaData={metaData} isLandingPage={true} />
        ) : (
            <HotelPage getEstabById={getEstabById} metaData={metaData} isLandingPage={true} />
        );
    }
    const { getPage } = data;
    return <CmsContentPage {...{ getPage }} />;
}

type LandingPageParams = {
    idString: string;
    slug: string[];
};

type LandingPageContext = GetServerSidePropsContext<LandingPageParams>;

type FacadeContentQueryParams = {
    props: GetFacadeContentQuery | HotelPageQuery;
};

export async function getServerSideProps(
    context: LandingPageContext
): Promise<FacadeContentQueryParams | RedirectPageParams | NotFoundParams> {
    context.res.setHeader('Cache-Control', 'public, max-age=600, stale-while-revalidate=3599, stale-while-error=3599');
    const params = context.params as LandingPageParams;
    const page = getPageFromIdString(params.idString);
    let data;
    const pagePath = `/${Object.values(params).join('/')}`;
    if (page.type === PageType.ESTAB) {
        data = await getPageAndGlobalProps<HotelPageQuery, LandingPageContext>(
            context,
            hotelPageQuery(page.estabId, page.pageStrand, true, pagePath)
        );

        const redirectUrl = data.props?.metaData?.redirectUrl;

        return redirectUrl ? isRedirectHotel(params.idString, params.slug, redirectUrl) || data : data;
    }

    data = await getPageAndGlobalProps<GetFacadeContentQuery, LandingPageContext>(
        context,
        getFacadeContentQuery(params.idString, params.slug || [], context.query?.isPreview)
    );

    const globalContents = data?.props?.globalContents as GlobalContent[];
    const pageRedirects: PageRedirects = globalContents?.find((item) => item?.key === 'REDIRECTS')
        ?.content as PageRedirects;
    return isRerouteGlobal(pagePath, pageRedirects) || isPageNotFound(data.props) || isRedirect(data.props) || data;
}
